import React from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';

const KpiTile = ({ icon, label, value, subtitle, errorTone = false }) => {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        textAlign: 'center',
        borderRadius: 2,
        bgcolor: errorTone ? alpha(theme.palette.error.main, 0.05) : '#F9F9FA',
        borderColor: errorTone ? alpha(theme.palette.error.main, 0.3) : 'divider',
      }}
    >
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.75} mb={0.5}>
        <Box sx={{ color: errorTone ? 'error.main' : 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            color: errorTone ? 'error.main' : 'text.secondary',
            fontSize: 10,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700} color={errorTone ? 'error.main' : 'text.primary'}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Paper>
  );
};

const ComplianceKpiCards = ({ summary }) => {
  const { misDashboard: m } = useTranslatedMessages();
  const processed = summary?.processed ?? 0;
  const approved = summary?.approved ?? 0;
  const errors = summary?.errors ?? 0;
  const ratePct = Math.round((summary?.approval_rate ?? 0) * 100);

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid size={{ xs: 4 }}>
        <KpiTile
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
          label={m.compliance?.processed ?? 'Processed'}
          value={processed}
          subtitle={'\u00A0'}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <KpiTile
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />}
          label={m.compliance?.approved ?? 'Approved'}
          value={approved}
          subtitle={(m.compliance?.approval_rate ?? '{{rate}}% rate').replace('{{rate}}', String(ratePct))}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <KpiTile
          icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
          label={m.compliance?.errors ?? 'Errors'}
          value={errors}
          subtitle={'\u00A0'}
          errorTone
        />
      </Grid>
    </Grid>
  );
};

export default ComplianceKpiCards;
